import React from "react";
import { ButtonOld } from "components/ButtonOld/ButtonOld";
import { ButtonGroup } from "components/ButtonOld/ButtonGroup";
import { DialogButtonPlain } from "gui/dialog/dialog";
import { ExpandableField } from "gui/input/fields";
import { FieldButtons } from "gui/input/fields";
import { FieldLabel } from "gui/input/fields";
import { Fields } from "gui/input/fields";
import { FullDashboard } from "lib/entries/types";
import { InputTextArea } from "gui/input/input";
import { TextWidget } from "lib/widgets/types";
import { VisualizationWidget } from "lib/widgets/types";
import { useErrorBoundary } from "lib/hooks/use-error-boundary";
import { useRequiredSession } from "gui/session/session";
import {
  callUpsertTextWidget,
  callRemoveTextWidget,
  callRemoveVisualizationWidget,
} from "./graphql";
import styles from "gui/widgets/entries.module.css";

export const WidgetsEditButton: React.FC<{
  dashboard: FullDashboard;
  textWidget?: TextWidget;
  visualizationWidget?: VisualizationWidget;
  refresh?: () => void;
}> = (props) => {
  const textWidget = props.textWidget;

  return (
    <DialogButtonPlain label="Edit dashboard" icon="edit" size="md">
      {textWidget !== undefined
        ? (onDismiss) => (
            <TextWidgetsEditDialog
              dashboard={props.dashboard}
              textWidget={textWidget}
              refresh={props.refresh}
              onDismiss={onDismiss}
            />
          )
        : (onDismiss) => (
            <VisualizationWidgetsEditDialog
              dashboard={props.dashboard}
              visualizationWidget={props.visualizationWidget!}
              refresh={props.refresh}
              onDismiss={onDismiss}
            />
          )}
    </DialogButtonPlain>
  );
};

const TextWidgetsEditDialog: React.FC<{
  dashboard: FullDashboard;
  textWidget: TextWidget;
  refresh?: () => void;
  onDismiss: () => void;
}> = (props) => {
  const [loading, setLoading] = React.useState(false);
  const [text, setText] = React.useState(props.textWidget.text ?? "");
  const session = useRequiredSession();
  const setError = useErrorBoundary();

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setLoading(true);
    callUpsertTextWidget(props.dashboard, props.textWidget.id, text, session)
      .then(() => props.refresh?.())
      .then(() => props.onDismiss())
      .catch(setError);
  };

  const onRemove = () => {
    setLoading(true);
    callRemoveTextWidget(props.textWidget.id, session)
      .then(() => props.refresh?.())
      .then(() => props.onDismiss())
      .catch(setError);
  };

  return (
    <form onSubmit={onSubmit}>
      <Fields>
        <TextWidgetFields text={text} setText={setText} />
        <FieldButtons>
          <ButtonOld
            type="submit"
            loading={loading}
            disabled={!text}
            color2
            size="sm"
          >
            Save
          </ButtonOld>
          <ButtonGroup>
            <ButtonOld
              onClick={() => onRemove()}
              loading={loading}
              color2
              light
              size="sm"
            >
              Remove this widget
            </ButtonOld>
            <ButtonOld
              onClick={props.onDismiss}
              disabled={loading}
              color2
              light
              size="sm"
            >
              Cancel
            </ButtonOld>
          </ButtonGroup>
        </FieldButtons>
      </Fields>
    </form>
  );
};

const VisualizationWidgetsEditDialog: React.FC<{
  dashboard: FullDashboard;
  visualizationWidget: VisualizationWidget;
  refresh?: () => void;
  onDismiss: () => void;
}> = (props) => {
  const [loading, setLoading] = React.useState(false);
  const session = useRequiredSession();
  const setError = useErrorBoundary();

  const onRemove = () => {
    setLoading(true);
    callRemoveVisualizationWidget(props.visualizationWidget.id, session)
      .then(() => props.refresh?.())
      .then(() => props.onDismiss())
      .catch(setError);
  };

  return (
    <Fields>
      <FieldButtons>
        <ButtonGroup>
          <ButtonOld
            onClick={() => onRemove()}
            loading={loading}
            color2
            light
            size="sm"
          >
            Remove this widget
          </ButtonOld>
          <ButtonOld
            onClick={props.onDismiss}
            disabled={loading}
            color2
            light
            size="sm"
          >
            Cancel
          </ButtonOld>
        </ButtonGroup>
      </FieldButtons>
    </Fields>
  );
};

export const TextWidgetFields: React.FC<{
  text: string;
  setText: (text: string) => void;
}> = (props) => {
  return (
    <FieldLabel label="Text widget context">
      <div>
        <InputTextArea
          value={props.text}
          onChange={(event) => props.setText(event.target.value)}
        />
        <ExpandableField
          label="Some markdown is supported"
          initialState="closed"
        >
          {markdownCheatSheet}
        </ExpandableField>
      </div>
    </FieldLabel>
  );
};

const markdownCheatSheet = (
  <div>
    <table className={styles.table}>
      <tbody>
        <tr className={styles.tr}>
          <th className={styles.th}>Bold</th>
          <td className={styles.td}>
            <b>**text**</b>
          </td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>Italic</th>
          <td className={styles.td}>
            <i>_text_</i>
          </td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>Heading 1</th>
          <td className={styles.td}># Text</td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>Heading 2</th>
          <td className={styles.td}>## Text</td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>Heading 3</th>
          <td className={styles.td}>### Text</td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>Link</th>
          <td className={styles.td}>
            [Link]
            <a href="https://dune.com">(https://dune.com)</a>
          </td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>Image or GIF</th>
          <td className={styles.td}>
            ![image]
            <a href="https://cutt.ly/1AKJVWx">(https://cutt.ly/1AKJVWx)</a>
          </td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>Inline code</th>
          <td className={styles.td}>
            `<code>code</code>`
          </td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>Code block</th>
          <td className={styles.td}>
            ```
            <br />
            <code>code block</code>
            <br />
            ```
          </td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>Horizontal rule</th>
          <td className={styles.td}>---</td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>Ordered list</th>
          <td className={styles.td}>
            <ol>
              <li>First item</li>
              <li>Second item</li>
              <li>Third item</li>
            </ol>
          </td>
        </tr>
        <tr className={styles.tr}>
          <th className={styles.th}>List</th>
          <td className={styles.td}>
            <ul>
              <li>- First item</li>
              <li>- Second item</li>
              <li>- Third item</li>
            </ul>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);
